import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import useConfig from "@/lib/useConfig";
import { Link, useLocation } from "react-router-dom";
import { useCountry } from "@/lib/country";

const QUICK = [
  "Explore Treatments",
  "Estimate Treatment Cost",
  "Find a Hospital",
  "Find a Doctor",
  "How Medical Travel Works",
  "Talk to a Care Coordinator",
];

function getSessionId() {
  let s = localStorage.getItem("mp_chat_session");
  if (!s) {
    s = "mp-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("mp_chat_session", s);
  }
  return s;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const boxRef = useRef(null);
  const { waLink } = useConfig();
  const { pathname } = useLocation();
  const { country } = useCountry();

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{
        role: "assistant",
        content: "Hi, I'm the MediPats Care Assistant.\n\nI can help you explore treatments, hospitals, doctors, estimated costs and the process of travelling to India for medical care.\n\nWhat can I help you with today?"
      }]);
    }
  }, [open]);

  useEffect(() => { boxRef.current?.scrollTo(0, 999999); }, [msgs, loading]);

  const send = async (text) => {
    const message = text ?? input;
    if (!message.trim() || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setLoading(true);
    try {
      const { data } = await http.post("/chat", {
        session_id: sessionId.current,
        message,
        page_context: { path: pathname, country: country?.slug || null, country_name: country?.name || null, currency: country?.currency || null },
      });
      setMsgs((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, something went wrong. Please try again or continue on WhatsApp." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 h-16 w-16 rounded-full flex items-center justify-center pulse-ring shadow-[0_12px_40px_-8px_rgba(180,122,154,0.6)] overflow-hidden"
        style={{ background: "linear-gradient(135deg,#2C1B2F,#0B0E1A)" }}
        data-testid="floating-ai-chatbot-button"
        aria-label="Open MediPats Care Assistant"
      >
        {open ? <X className="text-[#F3E9EC]"/> : <img src="/medipats-logo.png" alt="Open MediPats Care Assistant" className="w-11 h-11 object-contain"/>}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-md h-[70vh] max-h-[640px] rounded-2xl overflow-hidden glass flex flex-col" data-testid="ai-chatbot-panel">
          <div className="px-5 py-4 border-b border-[#5E3A5C]/30 flex items-center gap-3" style={{background: "linear-gradient(90deg,#2C1B2F,#0B0E1A)"}}>
            <img src="/medipats-logo.png" alt="MediPats" className="w-10 h-10 object-contain"/>
            <div className="flex-1">
              <div className="font-serif text-lg text-[#F3E9EC]">MediPats Care Assistant</div>
              <div className="text-[10px] uppercase tracking-widest text-[#B47A9A] font-mono">AI Concierge · Online</div>
            </div>
          </div>

          <div ref={boxRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#B47A9A] text-[#1A0F1B]" : "bg-[#F3E9EC]/95 text-[#1A0F1B]"
                }`} data-testid={`chat-msg-${m.role}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#F3E9EC]/95 text-[#1A0F1B] px-4 py-2.5 rounded-2xl text-sm">
                  <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 bg-[#5E3A5C] rounded-full animate-bounce"></span><span className="w-1.5 h-1.5 bg-[#5E3A5C] rounded-full animate-bounce" style={{animationDelay:".15s"}}></span><span className="w-1.5 h-1.5 bg-[#5E3A5C] rounded-full animate-bounce" style={{animationDelay:".3s"}}></span></span>
                </div>
              </div>
            )}
            {msgs.length <= 1 && (
              <div className="pt-2 flex flex-wrap gap-2">
                {QUICK.map(q => (
                  <button key={q} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-full border border-[#B47A9A]/40 text-[#F3E9EC]/85 hover:bg-[#B47A9A]/15" data-testid={`chat-quick-${q.replace(/\s+/g,'-').toLowerCase()}`}>{q}</button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#5E3A5C]/30 bg-[#0B0E1A]/70">
            <div className="flex items-center gap-2">
              <input
                value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && send()}
                placeholder="Ask about treatments, costs, hospitals..."
                className="mp-input flex-1 text-sm"
                data-testid="chat-input"
              />
              <button onClick={() => send()} className="btn-primary p-3" data-testid="chat-send"><Send className="w-4 h-4"/></button>
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-[#F3E9EC]/40">
              <span>General info only — not medical advice</span>
              <div className="flex gap-3">
                <Link to="/enquiry" className="text-[#B47A9A] hover:text-white" data-testid="chat-enquiry-link">Get Quote</Link>
                <a href={waLink("Hi MediPats, I was chatting with the Care Assistant and would like to continue on WhatsApp.")} target="_blank" rel="noreferrer" className="text-[#25D366]" data-testid="chat-whatsapp-link">WhatsApp</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
